import { useCallback, useEffect, useRef, useState } from 'react'
import { useSocketContext } from '@/context/SocketContext'
import type {
  CallCancelledEvent,
  CallEndedEvent,
  CallTakenEvent,
} from '@/services/chatSocket'

export type CallPhase = 'idle' | 'answering' | 'connecting' | 'connected'

export type CallErrorCode =
  | 'microphone_unavailable'
  | 'connection_failed'
  | 'answer_failed'

export interface ActiveCallInfo {
  callId: string
  contactPhone: string
  contactName: string
  messageDocId: string | null
}

// TODO(infra): no TURN server is configured yet — calls from agents behind a
// restrictive/corporate NAT (the normal case for a support team, not the
// exception) will fail to connect without one. Add TURN URLs here once
// provisioned; see the calling technical plan for why this is required, not
// optional.
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

const ICE_GATHERING_TIMEOUT_MS = 3000

function waitForIceGatheringComplete(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()

  return new Promise((resolve) => {
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timeout)
        pc.removeEventListener('icegatheringstatechange', onChange)
        resolve()
      }
    }
    const timeout = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', onChange)
      resolve()
    }, ICE_GATHERING_TIMEOUT_MS)
    pc.addEventListener('icegatheringstatechange', onChange)
  })
}

class MicrophoneUnavailableError extends Error {}

export function useCall() {
  const { ringingCalls, clearIncomingCall, setAgentOnCall, callActions } =
    useSocketContext()
  const [phase, setPhase] = useState<CallPhase>('idle')
  const [activeCall, setActiveCall] = useState<ActiveCallInfo | null>(null)
  const [connectedAt, setConnectedAt] = useState<number | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [error, setError] = useState<CallErrorCode | null>(null)

  const pcRef = useRef<RTCPeerConnection | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const activeCallRef = useRef<ActiveCallInfo | null>(null)
  activeCallRef.current = activeCall
  // Bumped on every reset so an answer still awaiting the mic, SDP or ICE
  // can tell the call it was answering has been cancelled, ended or taken.
  const answerAttemptRef = useRef(0)

  const cleanup = useCallback(() => {
    pcRef.current?.close()
    pcRef.current = null
    localStreamRef.current?.getTracks().forEach((track) => track.stop())
    localStreamRef.current = null
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null
    }
  }, [])

  const reset = useCallback(() => {
    answerAttemptRef.current += 1
    cleanup()
    setPhase('idle')
    setActiveCall(null)
    setConnectedAt(null)
    setIsMuted(false)
  }, [cleanup])

  useEffect(() => {
    const resetIfActive = (
      event: CallEndedEvent | CallCancelledEvent | CallTakenEvent,
    ) => {
      if (activeCallRef.current?.callId === event.callId) {
        reset()
      }
    }

    callActions.onCallEnded(resetIfActive)
    callActions.onCallCancelled(resetIfActive)
    callActions.onCallTaken(resetIfActive)

    return () => {
      callActions.offCallEnded(resetIfActive)
      callActions.offCallCancelled(resetIfActive)
      callActions.offCallTaken(resetIfActive)
    }
  }, [callActions, reset])

  useEffect(() => {
    setAgentOnCall(phase !== 'idle')
  }, [phase, setAgentOnCall])

  useEffect(
    () => () => {
      cleanup()
      setAgentOnCall(false)
    },
    [cleanup, setAgentOnCall],
  )

  const answer = useCallback(
    async (callId: string) => {
      if (activeCallRef.current) return

      const offer = ringingCalls.find((call) => call.callId === callId)
      if (!offer) return

      const attempt = ++answerAttemptRef.current
      const isStale = () => answerAttemptRef.current !== attempt

      setError(null)
      const callInfo: ActiveCallInfo = {
        callId: offer.callId,
        contactPhone: offer.contactPhone,
        contactName: offer.contactName,
        messageDocId: offer.messageDocId,
      }
      activeCallRef.current = callInfo
      setActiveCall(callInfo)
      setPhase('answering')

      try {
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
        pcRef.current = pc

        pc.ontrack = (event) => {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = event.streams[0] ?? null
          }
        }

        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'connected') {
            callActions.notifyCallMediaConnected({ callId: offer.callId })
            setPhase('connected')
            setConnectedAt(Date.now())
          } else if (pc.connectionState === 'failed') {
            callActions.hangupCall({ callId: offer.callId })
            setError('connection_failed')
            reset()
          }
        }

        const micStream = await navigator.mediaDevices
          .getUserMedia({ audio: true })
          .catch(() => {
            throw new MicrophoneUnavailableError()
          })
        if (isStale()) {
          micStream.getTracks().forEach((track) => track.stop())
          return
        }
        localStreamRef.current = micStream
        micStream.getTracks().forEach((track) => pc.addTrack(track, micStream))

        await pc.setRemoteDescription({ type: 'offer', sdp: offer.sdp })
        const localAnswer = await pc.createAnswer()
        await pc.setLocalDescription(localAnswer)

        // Meta's Calling API doesn't support trickle ICE — our answer has to
        // carry every candidate up front, or the call silently never connects.
        await waitForIceGatheringComplete(pc)
        if (isStale()) return

        const finalSdp = pc.localDescription?.sdp
        if (!finalSdp) {
          throw new Error('SDP local ausente após a negociação')
        }

        // Only claimed once the answer is actually sent: a local failure
        // above leaves the call in the queue so the agent can retry it.
        clearIncomingCall(offer.callId)
        setPhase('connecting')
        callActions.answerCall({ callId: offer.callId, sdp: finalSdp })
      } catch (err) {
        if (isStale()) return
        setError(
          err instanceof MicrophoneUnavailableError
            ? 'microphone_unavailable'
            : 'answer_failed',
        )
        reset()
      }
    },
    [ringingCalls, clearIncomingCall, callActions, reset],
  )

  const reject = useCallback(
    (callId: string) => {
      callActions.rejectCall({ callId })
      clearIncomingCall(callId)
    },
    [clearIncomingCall, callActions],
  )

  const hangup = useCallback(() => {
    if (!activeCall || phase === 'answering') return
    callActions.hangupCall({ callId: activeCall.callId })
    reset()
  }, [activeCall, phase, callActions, reset])

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current
    if (!stream) return

    setIsMuted((current) => {
      const next = !current
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !next
      })
      return next
    })
  }, [])

  const dismissError = useCallback(() => setError(null), [])

  const otherRingingCalls = ringingCalls.filter(
    (call) => call.callId !== activeCall?.callId,
  )

  return {
    incomingCall: phase === 'idle' ? (ringingCalls[0] ?? null) : null,
    otherRingingCount: phase === 'idle' ? Math.max(0, ringingCalls.length - 1) : 0,
    waitingCalls: phase === 'idle' ? [] : otherRingingCalls,
    phase,
    activeCall,
    connectedAt,
    isMuted,
    error,
    remoteAudioRef,
    answer,
    reject,
    hangup,
    toggleMute,
    dismissError,
  }
}
